import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Wallet, UserCircle, LogOut, Menu, X, Network,
  Briefcase, Trophy, MessageSquareText
} from 'lucide-react';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

const STATUS_STYLES = {
  active:    'bg-emerald-100 text-emerald-700',
  pending:   'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-600',
  rejected:  'bg-slate-100 text-slate-600',
};

export default function PartnerLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { partner, logout } = usePartnerAuth();
  const navigate = useNavigate();

  const isActive = partner?.status === 'active';

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/reseller/dashboard' },
    ...(isActive ? [
      { icon: Briefcase, label: 'Leads', path: '/reseller/leads' },
      { icon: Users, label: 'My Referrals', path: '/reseller/referrals' },
      { icon: Wallet, label: 'Wallet', path: '/reseller/wallet' },
      { icon: Trophy, label: 'Leaderboard', path: '/reseller/leaderboard' },
      { icon: MessageSquareText, label: 'SMS Marketing', path: '/reseller/sms' },
    ] : []),
    { icon: UserCircle, label: 'Profile', path: '/reseller/profile' },
  ];

  const initials = partner
    ? `${partner.first_name?.[0] || ''}${partner.last_name?.[0] || ''}`.toUpperCase()
    : 'P';
  const fullName = partner
    ? `${partner.first_name || ''} ${partner.last_name || ''}`.trim()
    : 'Partner';

  const handleLogout = () => {
    logout();
    navigate('/reseller/login');
  };

  return (
    <div className="flex h-screen bg-[#F8FAFC] overflow-hidden" data-testid="partner-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 lg:z-auto
        w-[220px] bg-white border-r border-slate-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-16 flex items-center px-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#516EFA] rounded-lg flex items-center justify-center flex-shrink-0">
              <Network className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-manrope font-bold text-slate-900 text-sm leading-tight">EDUVA</p>
              <p className="text-[10px] text-[#516EFA] font-medium leading-tight">Partner Network</p>
            </div>
          </div>
          <button className="ml-auto lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive: a }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-manrope transition-colors ${
                  a
                    ? 'bg-[#EDF0FF] text-[#516EFA] font-semibold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
              onClick={() => setSidebarOpen(false)}
              data-testid={`partner-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
              <span className="flex-1">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-100 p-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#EDF0FF] flex items-center justify-center text-[#516EFA] font-bold text-xs flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-800 truncate">{fullName}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[partner?.status] || 'bg-slate-100 text-slate-600'}`}>
                {partner?.program_type || 'partner'}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded hover:bg-slate-100"
              title="Logout"
              data-testid="partner-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-5 flex-shrink-0 z-20">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden text-slate-500 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <p className="text-sm font-semibold text-slate-800">Partner Portal</p>
              <p className="text-xs text-slate-400">
                Code: <span className="font-mono text-[#516EFA]">{partner?.partner_code}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#EDF0FF] flex items-center justify-center text-[#516EFA] font-bold text-xs">
              {initials}
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-semibold text-slate-800 leading-tight">{fullName}</p>
              <p className={`text-[10px] capitalize font-medium ${STATUS_STYLES[partner?.status] ? 'text-inherit' : 'text-slate-400'}`}>
                {partner?.status}
              </p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-[#F8FAFC]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
